import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Context } from './types';

interface Props {
  children: React.ReactNode;
}

const LanguageContext = createContext<Context>({} as Context);

export function LanguageProvider({ children }: Props) {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  useEffect(() => {
    const storedLanguage = localStorage.getItem('language');
    const browserLanguage = navigator?.languages?.[0]?.includes('fr')
      ? 'fr'
      : 'en';
    const initialLanguage = storedLanguage || browserLanguage;

    if (i18n.language !== initialLanguage) {
      i18n.changeLanguage(initialLanguage);
    }

    if (!storedLanguage) {
      localStorage.setItem('language', initialLanguage);
    }

    setLanguage(initialLanguage);
  }, []);

  useEffect(() => {
    const handleLanguageChange = (lang: string) => {
      return setLanguage(lang);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

const useLanguageContext = () => useContext(LanguageContext);
export default useLanguageContext;
